const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-API-Key': process.env.REACT_APP_API_KEY,
});

const handleResponse = async (res) => {
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(errorData.message || 'An unknown error occurred');
  }
  return res.json();
};

const apiRequest = (endpoint, method, body) => {
  const options = {
    method,
    headers: getHeaders(),
  };
  if (body) {
    options.body = JSON.stringify(body);
  }
  return fetch(endpoint, options).then(handleResponse);
};


export const analyzeTrademark = (businessDescription) => {
  return apiRequest('/api/quiz/analyze-tm', 'POST', { businessDescription });
};

export const calculatePrice = (requestData) => {
  return apiRequest('/api/quiz/calculate-price', 'POST', requestData);
};

export const fetchSessionData = (id, source) => {
  return apiRequest(`/api/quiz/session-data?id=${encodeURIComponent(id)}&source=${encodeURIComponent(source)}`, 'GET');
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
};

const setCookie = (name, value, days = 30) => {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}`;
};

/**
 * Capture and persist tracking params from URL into cookies on every page load.
 * Call once at app startup (e.g. in index.js or App.jsx).
 */
export const captureTrackingParams = () => {
  const params = new URLSearchParams(window.location.search);

  // Google Ads click IDs (incl. iOS wbraid/gbraid)
  const gclid = params.get('gclid') || params.get('wbraid') || params.get('gbraid');
  if (gclid) {
    setCookie('gclid', gclid);
  }

  // Meta click ID — fbclid from URL param
  const fbclid = params.get('fbclid');
  if (fbclid) {
    setCookie('fbclid', fbclid, 30);
  }

  // UTM parameters — only overwrite if present in current URL
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
    const val = params.get(key);
    if (val) setCookie(key, val);
  });
};

export const createLead = (leadData) => {
  // --- gclid: cookie → _gcl_aw fallback ---
  const storedGclid = getCookie('gclid');
  if (storedGclid) {
    leadData.gclid = storedGclid;
  } else {
    const gclAwCookie = getCookie('_gcl_aw');
    if (gclAwCookie) {
      const parts = gclAwCookie.split('.');
      if (parts.length === 3) {
        leadData.gclid = parts[2];
      }
    }
  }

  // --- GA4 client_id and session_id from cookies ---
  const gaCookie = getCookie('_ga');
  if (gaCookie) {
    const parts = gaCookie.split('.');
    if (parts.length >= 4) {
      const clientId = parts[2] + '.' + parts[3];
      leadData.ga_client_id = clientId;
      leadData.gaClientId = clientId;
    }
  }

  // GA4 session_id from _ga_<CONTAINER_ID> cookie
  const allCookies = document.cookie ? document.cookie.split('; ') : [];
  for (const c of allCookies) {
    const [name, val] = c.split('=');
    if (name && name.startsWith('_ga_')) {
      const parts = (val || '').split('.');
      if (parts.length >= 3 && parts[2]) {
        leadData.ga_session_id = parts[2];
        leadData.gaSessionId = parts[2];
        break;
      }
    }
  }

  // --- Meta browser/click IDs ---
  const fbp = getCookie('_fbp');
  if (fbp) leadData.fbp = fbp;

  const fbc = getCookie('_fbc');
  if (fbc) {
    leadData.fbc = fbc;
  } else {
    // Build fbc from fbclid cookie if _fbc not set
    const fbclid = getCookie('fbclid');
    if (fbclid) {
      leadData.fbc = `fb.1.${Date.now()}.${fbclid}`;
      leadData.fbclid = fbclid;
    }
  }

  // --- UTM parameters from cookies ---
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
    const val = getCookie(key);
    if (val && !leadData[key]) {
      leadData[key] = val;
    }
  });

  return apiRequest('/api/quiz/create-lead', 'POST', leadData);
};


export const initiatePayment = (paymentParams) => {
  return apiRequest('/api/quiz/payment', 'POST', paymentParams);
};
